// Sidebar footer: user profile, support links, language picker, logout, change-password dialog.
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Headphones, LogOut, MessageCircle, KeyRound } from "lucide-react";
import { LanguageSelector } from "@/components/LanguageSelector";

export function SidebarFooter({
  user, logout, onClose, handleNav, pushEnabled,
  showChangePw, setShowChangePw,
  pwForm, setPwForm, pwLoading, handleChangePw,
}) {
  const { t } = useTranslation();
  return (
    <div className="p-3 border-t border-border/50">
      <a href="https://wa.me/34600000000" target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-2 px-2 py-2 mb-1 rounded-lg text-xs text-gray-500 hover:bg-emerald-50 hover:text-emerald-700 transition"
        data-testid="support-whatsapp-link">
        <Headphones className="w-3.5 h-3.5" />
        <span>{t('common.nav.support_whatsapp')}</span>
      </a>
      <button
        onClick={() => handleNav("/support")}
        className="flex items-center gap-2 px-2 py-2 mb-2 rounded-lg text-xs text-gray-500 hover:bg-emerald-50 hover:text-emerald-700 transition w-full text-left"
        data-testid="support-tickets-link">
        <MessageCircle className="w-3.5 h-3.5" />
        <span>{t('common.nav.support_tickets')}</span>
      </button>
      {pushEnabled !== undefined && (
        <div className="flex items-center gap-1.5 mb-2 px-1">
          <div className={`w-1.5 h-1.5 rounded-full ${pushEnabled ? "bg-emerald-500" : "bg-gray-300"}`} />
          <span className="text-[10px] text-gray-400">{pushEnabled ? t('common.nav.notifications_active') : t('common.nav.notifications_disabled')}</span>
        </div>
      )}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
          <span className="text-sm font-bold text-emerald-700">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">{user?.name}</p>
          <p className="text-xs text-gray-500 truncate">{user?.email}</p>
        </div>
      </div>
      <div className="mb-3">
        <LanguageSelector className="w-full" />
      </div>
      <Button
        variant="secondary"
        className="w-full justify-start gap-2 h-9 text-xs"
        onClick={() => { logout(); if (onClose) onClose(); }}
        data-testid="logout-btn"
      >
        <LogOut className="w-3.5 h-3.5" />
        {t('auth.logout')}
      </Button>
      <Dialog open={showChangePw} onOpenChange={setShowChangePw}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" />Alterar Senha</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-600">Senha Atual</label>
              <Input type="password" value={pwForm.current} onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))} placeholder="Sua senha atual" data-testid="current-password" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Nova Senha</label>
              <Input type="password" value={pwForm.new_pw} onChange={e => setPwForm(p => ({ ...p, new_pw: e.target.value }))} placeholder="Min. 4 caracteres" data-testid="new-password" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Confirmar Nova Senha</label>
              <Input type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} placeholder="Repetir nova senha" data-testid="confirm-password" />
            </div>
            <Button onClick={handleChangePw} disabled={pwLoading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-password-btn">
              {pwLoading ? "Salvando..." : "Salvar Nova Senha"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
